/**
 * Note: the original design of this Card component was inspired by chakra-ui:
 * https://chakra-ui.com/docs/components/card/usage
 *
 * Our Card component is implemented using CSS grid instead of flexbox, which allows
 * [subgrid](https://web.dev/articles/css-subgrid) to optionally be used to align
 * content across multiple cards.
 */

import React from 'react'
import { ResponsiveClassNames, ResponsiveStyleProp, lit } from 'src/utils'
import { CardHeader } from './CardHeader'
import { CardMedia } from './CardMedia'
import { CardBody } from './CardBody'
import { CardFooter } from './CardFooter'
import { CardContext } from './CardContext'
import { DirectionOption, AlignmentOption } from './shared'
import type { Property as CSSProperty } from 'csstype'

export type { CardHeaderProps } from './CardHeader'
export type { CardMediaProps } from './CardMedia'
export type { CardFooterProps } from './CardFooter'

const directionClasses = new ResponsiveClassNames<DirectionOption>({
    base: {
        vertical: 'grid-flow-row',
        horizontal: 'grid-flow-col',
    },
    // anything larger than a phone in portrait mode
    '2xs': {
        vertical: '2xs:grid-flow-row',
        horizontal: '2xs:grid-flow-col',
    },
    md: {
        vertical: 'md:grid-flow-row',
        horizontal: 'md:grid-flow-col',
    },
    lg: {
        vertical: 'lg:grid-flow-row',
        horizontal: 'lg:grid-flow-col',
    },
})

const alignmentClasses = new ResponsiveClassNames<AlignmentOption>({
    base: {
        start: 'justify-items-start',
        center: 'justify-items-center',
        end: 'justify-items-end',
    },
    // anything larger than a phone in portrait mode
    '2xs': {
        start: '2xs:justify-items-start',
        center: '2xs:justify-items-center',
        end: '2xs:justify-items-end',
    },
    md: {
        start: 'md:justify-items-start',
        center: 'md:justify-items-center',
        end: 'md:justify-items-end',
    },
    lg: {
        start: 'lg:justify-items-start',
        center: 'lg:justify-items-center',
        end: 'lg:justify-items-end',
    },
})

export interface CardStylingProps {
    className?: string
    direction: ResponsiveStyleProp<DirectionOption>
    align: ResponsiveStyleProp<AlignmentOption>
    border?: 'neutral' | 'black'
    gridTemplateColumns?: ResponsiveStyleProp<CSSProperty.GridTemplateColumns>
    gridTemplateRows?: ResponsiveStyleProp<CSSProperty.GridTemplateRows>
    spacing: 'medium' | 'none'
}

export type CardProps = Partial<CardStylingProps> & {
    analyticsClassName?: string
    children: NonNullable<React.ReactNode>
    id?: string
}

const responsiveGridTemplateClassNames: {
    horizontal: Record<string, string>
    vertical: Record<string, string>
} = {
    horizontal: {
        base: 'grid-cols-[--cols-base]',
        '2xs': '2xs:grid-cols-[--cols-2xs]',
        md: 'md:grid-cols-[--cols-md]',
        lg: 'lg:grid-cols-[--cols-lg]',
    },
    vertical: {
        base: 'grid-rows-[--rows-base]',
        '2xs': '2xs:grid-rows-[--rows-2xs]',
        md: 'md:grid-rows-[--rows-md]',
        lg: 'lg:grid-rows-[--rows-lg]',
    },
}

const responsiveAutoGridClassNames: Record<string, Record<string, string>> = {
    base: {
        horizontal: 'auto-cols-[minmax(0,max-content)]',
        vertical: 'auto-rows-[minmax(0,max-content)]',
    },
    '2xs': {
        horizontal: '2xs:auto-cols-[minmax(0,max-content)]',
        vertical: '2xs:auto-rows-[minmax(0,max-content)]',
    },
    md: {
        horizontal: 'md:auto-cols-[minmax(0,max-content)]',
        vertical: 'md:auto-rows-[minmax(0,max-content)]',
    },
    lg: {
        horizontal: 'lg:auto-cols-[minmax(0,max-content)]',
        vertical: 'lg:auto-rows-[minmax(0,max-content)]',
    },
}

export function Card({
    direction = { base: 'vertical' },
    align = { base: 'start' },
    border,
    spacing = 'medium',
    gridTemplateColumns,
    gridTemplateRows,
    analyticsClassName = 'js_Card',
    className,
    children,
    id,
}: CardProps) {
    const directionClass = directionClasses.buildClassString(direction)
    const alignClass = alignmentClasses.buildClassString(align)
    const paddingClass = border ? 'p-5' : ''
    let borderClass = ''

    switch (border) {
        case 'neutral':
            borderClass = 'border-2 rounded-md border-neutral-200'
            break
        case 'black':
            borderClass = 'border rounded-md border-black'
            break
    }

    function buildGridTemplateClasses(directionOpt: DirectionOption) {
        const gridTemplateProp =
            directionOpt === 'horizontal'
                ? gridTemplateColumns
                : gridTemplateRows

        const classes = Object.entries(gridTemplateProp ?? {})
            .map(
                ([breakpoint, val]) =>
                    val &&
                    responsiveGridTemplateClassNames[directionOpt][breakpoint]
            )
            .filter(Boolean)

        if (classes.length) {
            return classes
        }

        // fall back to auto-columns or auto-rows as the default
        return Object.entries(direction)
            .map(
                ([breakpoint, val]) =>
                    val === directionOpt &&
                    responsiveAutoGridClassNames[breakpoint][val]
            )
            .filter(Boolean)
    }

    const gridTemplateClasses = [
        ...buildGridTemplateClasses('horizontal'),
        ...buildGridTemplateClasses('vertical'),
    ]

    function renderChild(child: React.ReactNode): React.ReactNode {
        if (!child) {
            return child
        }

        const cardSubComponents: React.JSXElementConstructor<any>[] = [
            CardHeader,
            CardMedia,
            CardBody,
            CardFooter,
        ]

        if (
            cardSubComponents.includes(
                (child as React.ReactElement)
                    .type as React.JSXElementConstructor<any>
            )
        ) {
            return child
        }

        if ((child as React.ReactElement).type === React.Fragment) {
            return React.Children.map(
                ((child as React.ReactElement).props as any).children,
                renderChild
            )
        }

        // If the programmer didn't already use CardBody, we wrap their body elements
        // in CardBody for them.
        //
        // We want to set the min-width and min-height of all children to zero
        // to prevent [grid blowouts](https://css-tricks.com/preventing-a-grid-blowout/).
        // (min-width and min-height are set in CardHeader, CardMedia, CardBody, and CardFooter)
        return <Card.Body>{child}</Card.Body>
    }

    return (
        <CardContext.Provider value={{ direction, align, spacing, border }}>
            <div
                id={id}
                className={lit`grid
                    ${gridTemplateClasses.join(' ')}
                    ${directionClass} ${alignClass} ${paddingClass} ${borderClass} ${className} ${analyticsClassName}`}
                style={
                    {
                        '--cols-base': gridTemplateColumns?.base,
                        '--cols-2xs': gridTemplateColumns?.['2xs'],
                        '--cols-md': gridTemplateColumns?.md,
                        '--cols-lg': gridTemplateColumns?.lg,
                        '--rows-base': gridTemplateRows?.base,
                        '--rows-2xs': gridTemplateRows?.['2xs'],
                        '--rows-md': gridTemplateRows?.md,
                        '--rows-lg': gridTemplateRows?.lg,
                    } as React.CSSProperties
                }
            >
                {/* We want to set the min-width and min-height of all children to zero
                    to prevent [grid blowouts](https://css-tricks.com/preventing-a-grid-blowout/).
                    (min-width is also set in CardHeader, CardMedia, and CardFooter)
                */}
                {React.Children.map(children, renderChild)}
            </div>
        </CardContext.Provider>
    )
}

Card.Header = CardHeader
Card.Media = CardMedia
Card.Body = CardBody
Card.Footer = CardFooter
